import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { GLASS } from "./CommandHeader";

export const RANGES = [["7d", "7 giorni"], ["30d", "30 giorni"], ["90d", "90 giorni"], ["12m", "12 mesi"]];

export function RangeFilter({ value, onChange, id }) {
  return (
    <div className="flex gap-1 rounded-xl border border-slate-800 bg-slate-900/80 p-1">
      {RANGES.map(([k, l]) => <button key={k} data-testid={`sa-${id}-filter-${k}`} onClick={() => onChange(k)} className={`rounded-lg px-2.5 py-1 font-mono text-[11px] transition-colors ${value === k ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>)}
    </div>
  );
}

const TREND = { up: [TrendingUp, "text-emerald-400", "+"], down: [TrendingDown, "text-red-400", ""], flat: [Minus, "text-slate-400", ""] };

const trendKey = (pct) => {
  if (pct > 0) return "up";
  if (pct < 0) return "down";
  return "flat";
};

function Delta({ cur, prev }) {
  if (!prev) return <span className="font-mono text-[11px] text-slate-500">{cur ? "nuovo" : "–"} vs periodo prec.</span>;
  const pct = Math.round(((cur - prev) / prev) * 100);
  const [I, tone, sign] = TREND[trendKey(pct)];
  return <span className={`flex items-center gap-1 font-mono text-[11px] ${tone}`}><I className="h-3 w-3" />{sign}{pct}% vs periodo prec.</span>;
}

function Kpi({ id, label, value, cur, prev, tag }) {
  return (
    <div data-testid={`sa-kpi-card-${id}`} className="group relative overflow-hidden rounded-2xl border border-[#00F0FF]/15 bg-slate-900/80 p-4 backdrop-blur-xl transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:border-[#00F0FF]/45 hover:shadow-[0_0_25px_rgba(0,240,255,0.15)] motion-reduce:hover:translate-y-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-2 font-mono text-2xl font-bold tracking-tight text-white" data-testid={`sa-kpi-value-${id}`}>{value}</p>
      <div className="mt-1 flex flex-col gap-1">{prev !== undefined && <Delta cur={cur} prev={prev} />}{tag && <span className="self-start rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 font-mono text-[9px] text-amber-300">{tag}</span>}</div>
    </div>
  );
}

function Commission({ pct, onSaved }) {
  const [v, setV] = useState(pct);
  useEffect(() => {
    setV(pct);
  }, [pct]);
  const save = () => api.put("/superadmin/cc/finance", { commission_pct: Number(v) }).then(() => { toast.success("Commissione aggiornata"); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <span className="flex items-center gap-2 font-mono text-[11px] text-slate-400">Commissione piattaforma
      <input data-testid="sa-commission-input" type="number" min={0} max={100} step={0.5} value={v} onChange={(e) => setV(e.target.value)} className="h-7 w-16 rounded-md border border-slate-700 bg-slate-950 px-2 text-slate-100" />%
      <button data-testid="sa-commission-save" onClick={save} className="rounded-md border border-[#00F0FF]/30 px-2 py-1 text-[#00F0FF] hover:bg-[#00F0FF]/10">Salva</button>
    </span>
  );
}

export function KpiGrid() {
  const [range, setRange] = useState("30d");
  const [k, setK] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get("/superadmin/cc/kpis", { params: { range } }).then(({ data }) => { setK(data); setErr(null); }).catch((e) => setErr(errMsg(e)));
  useEffect(() => {
    load();
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps
  const sim = k?.payments_source === "simulati" ? "SIMULATO" : null;
  const c = k?.current || {};
  const p = k?.previous || {};
  const s = k?.signups || {};
  const cards = k ? [
    ["utenti", "Utenti registrati", k.users_total], ["oggi", "Nuovi iscritti oggi", s.oggi, s.oggi, s.ieri], ["7g", "Iscrizioni 7 giorni", s["7g"], s["7g"], s["7g_prec"]],
    ["30g", "Iscrizioni 30 giorni", s["30g"], s["30g"], s["30g_prec"]], ["medici", "Medici", k.medici], ["infermieri", "Infermieri", k.infermieri], ["oss", "OSS", k.oss],
    ["verificati", "Professionisti verificati", k.verificati], ["prenotazioni", "Prenotazioni ricevute", c.prenotazioni, c.prenotazioni, p.prenotazioni],
    ["completate", "Prenotazioni completate", c.completate, c.completate, p.completate], ["cancellate", "Prenotazioni cancellate", c.cancellate, c.cancellate, p.cancellate],
    ["lordo", "Fatturato lordo", eur(c.lordo), c.lordo, p.lordo, sim], ["rimborsi", "Rimborsi", eur(c.rimborsi), c.rimborsi, p.rimborsi, sim],
    ["commissioni", "Commissioni piattaforma", eur(c.commissioni), c.commissioni, p.commissioni, sim], ["netto", "Ricavi netti piattaforma", eur(c.netto), c.netto, p.netto, sim],
  ] : [];
  return (
    <section className={`${GLASS} p-5`} data-testid="sa-kpi-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div><p className="font-display text-lg font-bold text-white">Panoramica in tempo reale</p><p className="text-xs text-slate-400">Dati reali dal database. Gli importi provengono da pagamenti {k?.payments_source || "…"}.</p></div>
        <RangeFilter id="kpi" value={range} onChange={setRange} />
      </div>
      {err && <p className="text-sm text-red-300" data-testid="sa-kpi-error">{err}</p>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-testid="sa-kpi-grid">
        {!k && !err && Array.from({ length: 10 }, (_, i) => <div key={`sk-${i}`} className="h-[104px] animate-pulse rounded-2xl bg-slate-800/60" />)}
        {cards.map(([id, l, v, cur, prev, tag]) => <Kpi key={id} id={id} label={l} value={v ?? 0} cur={cur} prev={prev} tag={tag} />)}
      </div>
      {k && <div className="mt-4 flex flex-wrap items-center justify-between gap-2"><Commission pct={k.commission_pct} onSaved={load} /><p className="font-mono text-[10px] text-slate-500">Ricavi netti = (lordo − rimborsi) × commissione</p></div>}
    </section>
  );
}
